import type { Playlist, PlaylistItem } from '../../types/playlist';
import { logDebug, logError } from '../logger';

export function debounce<T extends (...args: any[]) => void>(callback: T, wait = 200) {
  let timeout: NodeJS.Timeout | undefined;
  function debounced(...args: unknown[]) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => callback(...args), wait);
  }

  debounced.cancel = () => clearTimeout(timeout);
  return debounced;
}

export function throttle<T extends (...args: any[]) => unknown>(func: T, limit: number) {
  let lastFunc: NodeJS.Timeout | undefined;
  let lastRan: number | undefined;

  function throttled(this: ThisParameterType<T>, ...args: Parameters<T>): void {
    const timeSinceLastRan = lastRan ? Date.now() - lastRan : limit;

    if (timeSinceLastRan >= limit) {
      func.apply(this, args);
      lastRan = Date.now();
    } else if (!lastFunc) {
      lastFunc = setTimeout(() => {
        if (lastRan) {
          const timeSinceLastRan = Date.now() - lastRan;
          if (timeSinceLastRan >= limit) {
            func.apply(this, args);
            lastRan = Date.now();
          }
        }
        lastFunc = undefined;
      }, limit - timeSinceLastRan);
    }
  }

  throttled.cancel = () => clearTimeout(lastFunc);
  return throttled;
}

export const unicodeToChar = (text: string) => {
  return text.replace(/\\u[\dA-F]{4}/gi, (match) => {
    return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16));
  });
};

/**
 * Parse hex color and return the RGB colors
 * @param color
 * @return {{r: number, b: number, g: number}|undefined}
 */
export function hexToRgb(color: string) {
  if (color.indexOf('#') === 0) {
    color = color.slice(1);
  }

  // convert 3-digit hex to 6-digits.
  if (color.length === 3) {
    color = color[0] + color[0] + color[1] + color[1] + color[2] + color[2];
  }

  if (color.length !== 6) {
    return undefined;
  }

  return {
    r: parseInt(color.slice(0, 2), 16),
    g: parseInt(color.slice(2, 4), 16),
    b: parseInt(color.slice(4, 6), 16),
  };
}

/**
 * Utility to parse ARGB colors to hex.
 * @param argb
 */
export function argbToHex(argb: number | string): string {
  // Normalize input
  const hex = typeof argb === 'number' ? argb.toString(16).padStart(8, '0') : argb.replace(/^0x/i, '').padStart(8, '0');

  const a = parseInt(hex.slice(0, 2), 16);

  // Fully opaque → return simple hex
  if (a !== 255) {
    logDebug('argbToHex', `Parsing ${argb} contains an alpha which is not supported`);
  }

  return `#${hex.slice(2)}`;
}

/**
 * Note: this function is generated by AI
 */
function relativeLuminance({ r, g, b }: { r: number; g: number; b: number }): number {
  const srgb = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
}

/**
 * Note: this function is generated by AI
 */
function contrastRatio(l1: number, l2: number): number {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Get the contrast color based on the given color.
 * Note: this function is generated by AI
 *
 * @param {string} color Hex or RGBA color string
 * @param {number} [opacity] Optional opacity for the contrast color
 * @return {string}
 */
export function calculateContrastColor(color: string, opacity?: number): string {
  const rgb = hexToRgb(color);
  if (!rgb) return '';

  const bgLum = relativeLuminance(rgb);
  const whiteLum = 1;
  const blackLum = 0;

  const whiteContrast = contrastRatio(bgLum, whiteLum);
  const blackContrast = contrastRatio(bgLum, blackLum);

  const useBlack = blackContrast >= whiteContrast;

  if (opacity !== undefined) {
    return useBlack ? `rgba(0, 0, 0, ${opacity})` : `rgba(255, 255, 255, ${opacity})`;
  }

  return useBlack ? '#000000' : '#FFFFFF';
}

// Build is either Development or Production
// Mode can be dev, jwdev, demo, test, prod, etc.
export const IS_DEVELOPMENT_BUILD = __dev__;
// Demo mode is used to run our firebase demo instance
export const IS_DEMO_MODE = __mode__ === 'demo';
// Test mode is used for e2e and unit tests
export const IS_TEST_MODE = __mode__ === 'test';

// Preview mode is used for previewing Pull Requests on GitHub
export const IS_PREVIEW_MODE = __mode__ === 'preview';
// Production mode
export const IS_PROD_MODE = __mode__ === 'prod';

export const isContentType = (item: PlaylistItem | Playlist, contentType: string) => item.contentType?.toLowerCase() === contentType.toLowerCase();

export const isTruthyCustomParamValue = (value: unknown): boolean => ['true', '1', 'yes', 'on'].includes(String(value)?.toLowerCase());

export const isFalsyCustomParamValue = (value: unknown): boolean => ['false', '0', 'no', 'off'].includes(String(value)?.toLowerCase());

export function testId(value: string | undefined) {
  return IS_DEVELOPMENT_BUILD || IS_TEST_MODE || IS_PREVIEW_MODE ? value : undefined;
}

type Truthy<T> = T extends false | '' | 0 | null | undefined ? never : T;
export const isTruthy = <T>(value: T | true): value is Truthy<T> => Boolean(value);

// This util makes sure the query takes at least x milliseconds long
// This is useful, i.e., to have a loading spinner for a minimum amount of time
export const minimalLoadTimeFn = <Args extends unknown[], Result>(fn: (...args: Args) => Promise<Result>, loadTimeMs: number) => {
  return async (...args: Args): Promise<Result> => {
    const startTime = Date.now();
    const result = await fn(...args);
    const elapsed = Date.now() - startTime;
    const delay = Math.max(loadTimeMs - elapsed, 0);

    await new Promise((resolve) => setTimeout(resolve, delay));

    return result;
  };
};

export const isFulfilled = <T>(input: PromiseSettledResult<T>): input is PromiseFulfilledResult<T> => {
  if (input.status === 'fulfilled') {
    return true;
  }

  logError('EpgController', `An error occurred resolving a promise: `, { error: input.reason });
  return false;
};

export const allSettledValues = async <T>(promises: Promise<T>[]) => {
  const settled = await Promise.allSettled(promises);

  return settled.filter(isFulfilled).map((result) => result.value);
};
